import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Request, RequestFile } from 'src/app/request-v2/models/request.model';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';
import { isImageUrl } from 'src/app/request-v2/utils/request-file.util';
import { UserService } from 'src/app/user/service/user.abstract.service';

@Component({
    selector: 'app-request-internal-files',
    templateUrl: './request-internal-files.component.html',
    styleUrls: ['./request-internal-files.component.scss'],
})
export class RequestInternalFilesComponent {
    @Input() request: Request;
    @Input() canEdit = false;
    @Output() requestChanged = new EventEmitter<Request>();

    @ViewChild('fileInput') fileInput: ElementRef;

    isUploading = false;
    isDeleting = false;
    isDragOver = false;
    private dragCounter = 0;

    constructor(
        private userService: UserService,
        private requestsV2Service: RequestsV2ImplService,
    ) {}

    get internalFiles(): RequestFile[] {
        return this.request?.internalFiles || [];
    }

    downloadFile(file: RequestFile): void {
        let parsedUrl: URL;

        try {
            parsedUrl = new URL(file.url, window.location.origin);
        } catch (_error) {
            return;
        }

        if (!['http:', 'https:', 'blob:'].includes(parsedUrl.protocol)) {
            return;
        }

        const anchor = document.createElement('a');
        anchor.href = parsedUrl.toString();
        anchor.download = this.getFileName(file);
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    isImage(file: RequestFile): boolean {
        return isImageUrl(file.url);
    }

    isDocumentPdf(file: RequestFile): boolean {
        return /\.pdf($|\?)/i.test(file.url);
    }

    isDocumentWord(file: RequestFile): boolean {
        return /\.(doc|docx)($|\?)/i.test(file.url);
    }

    isDocumentExcel(file: RequestFile): boolean {
        return /\.(xls|xlsx)($|\?)/i.test(file.url);
    }

    getFileName(file: RequestFile): string {
        if (file.originalName) {
            return file.originalName;
        }
        try {
            const urlParts = file.url.split('/');
            const fileName = urlParts[urlParts.length - 1];
            return fileName.split('?')[0];
        } catch (_error) {
            return file.url;
        }
    }

    @HostListener('document:dragenter', ['$event'])
    onDocumentDragEnter(event: DragEvent): void {
        if (event.dataTransfer?.types?.includes('Files')) {
            this.dragCounter++;
            this.isDragOver = true;
        }
    }

    @HostListener('document:dragleave', ['$event'])
    onDocumentDragLeave(): void {
        this.dragCounter = Math.max(0, this.dragCounter - 1);
        if (this.dragCounter === 0) this.isDragOver = false;
    }

    @HostListener('document:dragover', ['$event'])
    onDocumentDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    @HostListener('document:drop', ['$event'])
    onDocumentDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragCounter = 0;
        this.isDragOver = false;
    }

    onDrop(event: DragEvent): void {
        const files = Array.from(event.dataTransfer?.files || []);
        if (files.length > 0) this.uploadFiles(files);
    }

    addFile(): void {
        if (this.isUploading) return;
        this.fileInput.nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        const files = Array.from(target.files || []);
        target.value = '';
        if (files.length > 0) this.uploadFiles(files);
    }

    private uploadFiles(files: File[]): void {
        if (!this.request?._id) return;
        this.isUploading = true;
        forkJoin(files.map((f) => this.userService.postDocuments(f).pipe(catchError(() => of(null))))).subscribe({
            next: (responses: (string[] | null)[]) => {
                const fileData = responses.map((urls, i) => (urls?.length > 0 ? { url: urls[0], originalName: files[i].name } : null)).filter(Boolean);
                if (fileData.length === 0) {
                    this.isUploading = false;
                    return;
                }
                this.requestsV2Service.attachInternalFile(this.request._id, fileData).subscribe({
                    next: (updatedRequest) => {
                        this.request = updatedRequest;
                        this.requestChanged.emit(updatedRequest);
                        this.isUploading = false;
                    },
                    error: () => {
                        this.isUploading = false;
                    },
                });
            },
            error: () => {
                this.isUploading = false;
            },
        });
    }

    removeFile(index: number): void {
        if (!this.request?._id || this.isUploading || this.isDeleting) {
            return;
        }

        this.isDeleting = true;

        this.requestsV2Service.deleteInternalFile(this.request._id, index).subscribe({
            next: (updatedRequest) => {
                this.request = updatedRequest;
                this.requestChanged.emit(updatedRequest);
                this.isDeleting = false;
            },
            error: () => {
                this.isDeleting = false;
            },
        });
    }
}
